/**
 * Bow catalog with Sparrow Expeditions titanium-kit fitments.
 *
 * Drives the "Add titanium kit for your bow" upsell in the Build → Cart card
 * AND the My Bow preloader in the Tuning Wizard.
 *
 * Each entry has the bow's spec sheet + (where stocked) the Shopify variant
 * ID for the matching Firenock titanium kit. IBO speed and let-off are also
 * surfaced so the bow-model picker can auto-fill those fields in the bow
 * setup view.
 *
 * Verification: specs sourced from manufacturer product pages
 * (mathewsinc.com, hoyt.com, psearchery.com, bowtecharchery.com,
 * elitearchery.com, beararchery.com, g5prime.com, dartonarchery.com) and
 * Lancaster Archery Supply spec sheets. Last refreshed May 2026.
 */

export interface BowModel {
  brand: string;
  model: string;
  /** Model year — used for display + sorting newest-first */
  year: number;
  /** IBO speed (ATA-rated fps at 70lb / 30" / 350gr) */
  iboSpeed: number;
  /** Stock let-off percentage */
  letOff: number;
  /** Axle-to-axle length in inches */
  axleToAxle?: number;
  /** Brace height in inches */
  brace?: number;
  /** Physical weight (lbs) */
  weightLb?: number;
  /** Draw weight range (min, max in lbs) */
  drawWeightRange?: [number, number];
  /** Draw length range (min, max in inches) */
  drawLengthRange?: [number, number];
  /**
   * Bow category — 'hunting' or 'target'. Drives picker filtering in the
   * Tuning wizard + Preflight step. Defaults to 'hunting' (most of catalog).
   */
  category?: 'hunting' | 'target';
  /** Shopify variant ID for the titanium kit (for /cart/{id}:1 deep links) */
  titaniumKitVariantId?: number;
  /** Price in USD (string for direct display) */
  titaniumKitPrice?: string;
  /** Shopify product handle (for SEO-friendly product links) */
  titaniumKitProductHandle?: string;
  /**
   * Cam-lean / lateral-nock-travel adjustment system for this bow's platform —
   * the "shift the cams" step of paper tuning. Compact human string (e.g.
   * 'Limb Shift (no yokes/top hats)', 'DeadLock cam (screw shifts cam laterally,
   * no press)', 'top-hat spacers'). Populated ONLY where verified against the
   * manufacturer / a reputable tech source, or confirmed by Sal. Absent = we
   * have not verified this platform → surfaces as "check your model's manual /
   * pro shop" (never guessed). Additive/optional; carried by lib:sync.
   */
  tuning?: string;
}

/**
 * Stocked bow models — 2022-2026 flagships from the major brands, with every
 * ATA variant per platform. Last 4 years deep because most hunters upgrade
 * every 3-4 years and the tuning wizard needs to recognize their bow.
 */
export const BOWS: readonly BowModel[] = [
  // ─────────────────────────────────────────────────────────────────
  // MATHEWS — V3X (2022) → Phase 4 (2023) → Lift (2024) → Lift X (2025) → ARC (2026)
  // Every model has 29/29.5" and 33/34" ATA variants.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Mathews', model: 'V3X 29', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 29, brace: 6.0,
    weightLb: 4.47, drawWeightRange: [60, 75], drawLengthRange: [25.5, 30],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'V3X 33', year: 2022, iboSpeed: 336, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.67, drawWeightRange: [60, 75], drawLengthRange: [27, 31.5],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Phase 4 29', year: 2023, iboSpeed: 336, letOff: 80, axleToAxle: 29, brace: 6.0,
    weightLb: 4.48, drawWeightRange: [60, 75], drawLengthRange: [25.5, 30],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Phase 4 33', year: 2023, iboSpeed: 336, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.68, drawWeightRange: [60, 75], drawLengthRange: [27, 31.5],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Lift 29.5', year: 2024, iboSpeed: 348, letOff: 80, axleToAxle: 29.5, brace: 6.0,
    weightLb: 3.99, drawWeightRange: [55, 80], drawLengthRange: [24.5, 30],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Lift 33', year: 2024, iboSpeed: 343, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.26, drawWeightRange: [55, 80], drawLengthRange: [26, 31.5],
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Lift X 29.5', year: 2025, iboSpeed: 348, letOff: 80, axleToAxle: 29.5, brace: 6.0,
    weightLb: 3.99, drawWeightRange: [55, 80], drawLengthRange: [24.5, 30],
    tuning: 'Limb Shift (no yokes/top hats)' },
  { brand: 'Mathews', model: 'Lift X 33', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.26, drawWeightRange: [55, 80], drawLengthRange: [26, 31.5],
    tuning: 'Limb Shift (no yokes/top hats)' },
  // Lift RS = short-draw / compact 2025 Lift (verified mathewsinc.com); IBO 353 (343 @ 29").
  { brand: 'Mathews', model: 'Lift RS', year: 2025, iboSpeed: 353, letOff: 80, axleToAxle: 27.75, brace: 5.5,
    weightLb: 3.92, drawWeightRange: [41, 80], drawLengthRange: [23.5, 29],
    tuning: 'Limb Shift (no yokes/top hats)' },
  // Lift XD = long-draw 2025 Lift (SwitchWeight X+ cam); verified mathewsinc.com.
  { brand: 'Mathews', model: 'Lift XD', year: 2025, iboSpeed: 363, letOff: 80, axleToAxle: 33, brace: 6.75,
    weightLb: 4.31, drawWeightRange: [50, 75], drawLengthRange: [32, 33.5],
    tuning: 'Limb Shift (no yokes/top hats)' },
  /* Mathews ARC 30 + 34 — 2026 SWX-2 cam platform.
   * Specs verified against mathewsinc.com + lancasterarchery.com June 2026. */
  { brand: 'Mathews', model: 'ARC 30', year: 2026, iboSpeed: 348, letOff: 80, axleToAxle: 30, brace: 6.0,
    weightLb: 3.99, drawWeightRange: [55, 80], drawLengthRange: [25, 30.5],
    titaniumKitVariantId: 47844945658082, titaniumKitPrice: '129.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-mathews-2026-arc-30-arc-34',
    tuning: 'Limb Shift (no yokes/top hats)' },
  { brand: 'Mathews', model: 'ARC 34', year: 2026, iboSpeed: 343, letOff: 80, axleToAxle: 34, brace: 6.5,
    weightLb: 4.3, drawWeightRange: [55, 80], drawLengthRange: [26.5, 32],
    titaniumKitVariantId: 47844945658082, titaniumKitPrice: '129.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-mathews-2026-arc-30-arc-34',
    tuning: 'Limb Shift (no yokes/top hats)' },

  // ─────────────────────────────────────────────────────────────────
  // HOYT — Carbon RX-7 / RX-8 / RX-9 (carbon) and VTM / Alpha X / AX-2 / AX-3 (aluminum)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Hoyt', model: 'Carbon RX-7', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.25,
    weightLb: 3.9, drawWeightRange: [30, 80], drawLengthRange: [25, 30],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-7 Ultra', year: 2023, iboSpeed: 338, letOff: 80, axleToAxle: 34, brace: 6.75,
    weightLb: 4.2, drawWeightRange: [30, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'VTM 31', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.25,
    weightLb: 4.4, drawWeightRange: [40, 80], drawLengthRange: [25, 30],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'VTM 34', year: 2023, iboSpeed: 338, letOff: 80, axleToAxle: 34, brace: 6.75,
    weightLb: 4.6, drawWeightRange: [40, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Alpha X 30', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.125,
    weightLb: 4.4, drawWeightRange: [40, 80], drawLengthRange: [25, 30],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Alpha X 33', year: 2024, iboSpeed: 338, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.6, drawWeightRange: [40, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-8', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.125,
    weightLb: 4.0, drawWeightRange: [30, 80], drawLengthRange: [25, 30],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-8 Ultra', year: 2024, iboSpeed: 338, letOff: 80, axleToAxle: 33.5, brace: 6.5,
    weightLb: 4.2, drawWeightRange: [30, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Alpha AX-2 29', year: 2025, iboSpeed: 340, letOff: 80, axleToAxle: 29, brace: 6.0,
    weightLb: 4.4, drawWeightRange: [40, 80], drawLengthRange: [24.5, 30],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Alpha AX-2 33', year: 2025, iboSpeed: 338, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.6, drawWeightRange: [40, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'RX-9', year: 2025, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.0,
    weightLb: 3.9, drawWeightRange: [30, 80], drawLengthRange: [25, 30],
    titaniumKitVariantId: 47844946968802, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-hoyt-2025-rx-9-carbon-rx-9',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-9', year: 2025, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.0,
    weightLb: 3.9, drawWeightRange: [30, 80], drawLengthRange: [25, 30],
    titaniumKitVariantId: 47844946968802, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-hoyt-2025-rx-9-carbon-rx-9',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-9 Ultra', year: 2025, iboSpeed: 340, letOff: 80, axleToAxle: 33.5, brace: 6.5,
    weightLb: 4.1, drawWeightRange: [30, 80], drawLengthRange: [26.5, 31],
    tuning: 'HBX cam spacers' },
  // 2026 Hoyt — aluminum hunting flagships: AX-3 29 + AX-3 33 + AX-90.
  // Carbon flagships: RX-10 (short) + RX-10 Ultra (long ATA).
  // All run HBX Gen 4 cam + patent-pending XTS Tuning System (corrects high
  // AND low tears no-press, unique to 2026 Hoyt). Verified hoyt.com June 2026.
  // AX-3 33 LD and AX-3 29 SD both re-verified end-to-end against hoyt.com
  // June 2026 — both had multiple wrong specs (ibo/brace/weight/draw), all
  // corrected below. Full AX-3 family now matches the manufacturer spec block.
  /* Hoyt AX-3 29 — verified specs from hoyt.com June 2026: 340 fps, 29.5" ATA,
   * 6.375" brace, 4.4 lb, 40-80 lb draw weight, 25-30" draw length. */
  { brand: 'Hoyt', model: 'Alpha AX-3 29', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 29.5, brace: 6.375,
    weightLb: 4.4, drawWeightRange: [40, 80], drawLengthRange: [25, 30],
    titaniumKitVariantId: 47844946641122, titaniumKitPrice: '148.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-hoyt-2026-alpha-ax-3',
    tuning: 'XTS wedge' },
  // SD = Short Draw module set (23-27" draw length range). Same riser as AX-3 29,
  // different cam module — for archers with shorter draws who still want the
  // 29.5" ATA hunting platform.
  { brand: 'Hoyt', model: 'Alpha AX-3 29 SD (Short Draw)', year: 2026, iboSpeed: 310, letOff: 80, axleToAxle: 29.5, brace: 6.0,
    weightLb: 4.25, drawWeightRange: [30, 70], drawLengthRange: [23, 27],
    titaniumKitVariantId: 47844946641122, titaniumKitPrice: '148.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-hoyt-2026-alpha-ax-3',
    tuning: 'XTS wedge' },
  /* Hoyt AX-3 33 — verified specs from hoyt.com June 2026: 340 fps, 33 9/16" ATA,
   * 6.375" brace, 4.75 lb, 40-80 lb draw weight, 26-31" draw length. */
  { brand: 'Hoyt', model: 'Alpha AX-3 33', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 33.5625, brace: 6.375,
    weightLb: 4.75, drawWeightRange: [40, 80], drawLengthRange: [26, 31],
    tuning: 'XTS wedge' },
  // LD = Long Draw module set. 33 LD is for tall archers who want max draw on
  // the longer-brace 33" platform. Verified hoyt.com June 2026: 355 fps,
  // 33 1/4" ATA, 7 5/8" brace, 4.85 lb, 31 1/4-33" draw, 40-80 lb.
  { brand: 'Hoyt', model: 'Alpha AX-3 33 LD (Long Draw)', year: 2026, iboSpeed: 355, letOff: 80, axleToAxle: 33.25, brace: 7.625,
    weightLb: 4.85, drawWeightRange: [40, 80], drawLengthRange: [31.25, 33],
    tuning: 'XTS wedge' },
  // 2026 Alpha AX-90 — 90 lb draw weight bow for dangerous game / heavy hunters.
  { brand: 'Hoyt', model: 'Alpha AX-90', year: 2026, iboSpeed: 348, letOff: 80, axleToAxle: 31, brace: 6.25,
    weightLb: 4.9, drawWeightRange: [60, 90], drawLengthRange: [26, 31],
    tuning: 'XTS wedge' },
  // 2026 Carbon RX-10 — flagship carbon riser. RX-10 = shorter ATA (30"); Ultra = longer ATA (33.5").
  // SD/LD variants are for short-draw + long-draw archers respectively — same riser, different cam modules.
  /* Hoyt Carbon RX-10 lineup — verified June 2026 against hoyt.com.
   * Draw weight range standardized to 40-80 lb across all four variants. */
  { brand: 'Hoyt', model: 'Carbon RX-10', year: 2026, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.125,
    weightLb: 4.0, drawWeightRange: [40, 80], drawLengthRange: [25, 30],
    tuning: 'XTS wedge' },
  { brand: 'Hoyt', model: 'Carbon RX-10 SD (Short Draw)', year: 2026, iboSpeed: 332, letOff: 80, axleToAxle: 30, brace: 6.125,
    weightLb: 4.0, drawWeightRange: [40, 80], drawLengthRange: [23, 27],
    tuning: 'XTS wedge' },
  { brand: 'Hoyt', model: 'Carbon RX-10 Ultra', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 33.5, brace: 6.375,
    weightLb: 4.5, drawWeightRange: [40, 80], drawLengthRange: [26, 31],
    tuning: 'XTS wedge' },
  { brand: 'Hoyt', model: 'Carbon RX-10 Ultra LD (Long Draw)', year: 2026, iboSpeed: 344, letOff: 80, axleToAxle: 33.5, brace: 6.375,
    weightLb: 4.5, drawWeightRange: [40, 80], drawLengthRange: [29, 32.5],
    tuning: 'XTS wedge' },

  // ─────────────────────────────────────────────────────────────────
  // PSE — Mach + EVO XF platforms, with EC2 (2023-2025) → FDS (2026 DS) cam transition
  // ─────────────────────────────────────────────────────────────────
  { brand: 'PSE', model: 'Mach 30', year: 2023, iboSpeed: 340, letOff: 85, axleToAxle: 30, brace: 6.5,
    weightLb: 3.85, drawWeightRange: [50, 80], drawLengthRange: [24.5, 30] },
  { brand: 'PSE', model: 'Mach 33', year: 2023, iboSpeed: 335, letOff: 85, axleToAxle: 33, brace: 7.0,
    weightLb: 3.65, drawWeightRange: [50, 80], drawLengthRange: [27, 31] },
  { brand: 'PSE', model: 'Mach 34', year: 2023, iboSpeed: 335, letOff: 85, axleToAxle: 34, brace: 7.125,
    weightLb: 3.65, drawWeightRange: [50, 80], drawLengthRange: [29, 33] },
  { brand: 'PSE', model: 'EVO XF 30', year: 2024, iboSpeed: 336, letOff: 85, axleToAxle: 30, brace: 6.5,
    weightLb: 3.95, drawWeightRange: [50, 80], drawLengthRange: [24.5, 30] },
  { brand: 'PSE', model: 'EVO XF 33', year: 2024, iboSpeed: 332, letOff: 85, axleToAxle: 33, brace: 6.75,
    weightLb: 4.05, drawWeightRange: [50, 80], drawLengthRange: [26, 31] },
  { brand: 'PSE', model: 'Mach 30 DS', year: 2025, iboSpeed: 342, letOff: 85, axleToAxle: 30, brace: 6.5,
    weightLb: 3.85, drawWeightRange: [50, 80], drawLengthRange: [24.5, 30],
    tuning: 'EZ.220 cam shims' },
  { brand: 'PSE', model: 'Mach 33 DS', year: 2025, iboSpeed: 338, letOff: 85, axleToAxle: 33, brace: 7.0,
    weightLb: 3.65, drawWeightRange: [50, 80], drawLengthRange: [27, 31],
    tuning: 'EZ.220 cam shims' },
  { brand: 'PSE', model: 'Lightning', year: 2025, iboSpeed: 350, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 3.9, drawWeightRange: [50, 80], drawLengthRange: [25, 30] },
  { brand: 'PSE', model: 'Shootdown Pro', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 33, brace: 7.0,
    weightLb: 4.0, drawWeightRange: [50, 80], drawLengthRange: [27, 31],
    titaniumKitVariantId: 47844947067106, titaniumKitPrice: '124.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-pse-2024-shootdown-pro',
    tuning: 'EZ.220 cam shims' },
  { brand: 'PSE', model: 'Evolve', year: 2024, iboSpeed: 332, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 4.1, drawWeightRange: [50, 80], drawLengthRange: [25, 30],
    titaniumKitVariantId: 47844947132642, titaniumKitPrice: '174.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-pse-2024-25-evolve-decree' },
  { brand: 'PSE', model: 'Decree', year: 2024, iboSpeed: 336, letOff: 80, axleToAxle: 32, brace: 6.5,
    weightLb: 4.0, drawWeightRange: [50, 80], drawLengthRange: [26, 30.5],
    titaniumKitVariantId: 47844947132642, titaniumKitPrice: '174.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-pse-2024-25-evolve-decree' },
  /* PSE 2026 Mach DS lineup — FDS cam platform (replaces EC2). Adjustable
   * let-off 70/75/80/85%. Specs verified June 2026 against psearchery.com.
   * Carbon-riser variants (Mach 30 DS Carbon, Mach 33 DS Carbon FDS) exist
   * with same ATA/brace; add separately if needed for that audience. */
  { brand: 'PSE', model: 'Mach 30 DS', year: 2026, iboSpeed: 348, letOff: 85, axleToAxle: 30, brace: 5.875,
    weightLb: 3.6, drawWeightRange: [50, 80], drawLengthRange: [24.5, 30],
    tuning: 'EZ.220 cam shims' },
  { brand: 'PSE', model: 'Mach 33 DS', year: 2026, iboSpeed: 342, letOff: 85, axleToAxle: 33, brace: 6.625,
    weightLb: 3.65, drawWeightRange: [50, 80], drawLengthRange: [27, 31],
    tuning: 'EZ.220 cam shims' },
  /* PSE Sicario lineup 2026 — DFC carbon riser + FDS cam. Two ATAs.
   * Specs verified against psearchery.com + lancasterarchery.com product pages June 2026. */
  { brand: 'PSE', model: 'Sicario FDS 33', year: 2026, iboSpeed: 357, letOff: 80, axleToAxle: 33, brace: 5.25,
    weightLb: 3.9, drawWeightRange: [50, 80], drawLengthRange: [24.5, 30],
    tuning: 'EZ.220 shims + split yokes (shims are primary)' },
  { brand: 'PSE', model: 'Sicario FDS 35', year: 2026, iboSpeed: 336, letOff: 80, axleToAxle: 35, brace: 5.875,
    weightLb: 3.9, drawWeightRange: [50, 80], drawLengthRange: [26, 31.5],
    tuning: 'EZ.220 shims + split yokes (shims are primary)' },

  // ─────────────────────────────────────────────────────────────────
  // BOWTECH — SR350 (2023) → CP30 / Eva Gen3 (2024) → Carbon One / Reckoning Gen2 / Solution SS (2024-25) → Proven 34 (2025)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Bowtech', model: 'SR350', year: 2023, iboSpeed: 350, letOff: 80, axleToAxle: 32, brace: 6.125,
    weightLb: 4.5, drawWeightRange: [50, 70], drawLengthRange: [26, 31],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'CP30', year: 2024, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25, 30],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Carbon One', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.125,
    weightLb: 3.8, drawWeightRange: [40, 70], drawLengthRange: [25, 30],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Eva Gen3', year: 2024, iboSpeed: 322, letOff: 80, axleToAxle: 28, brace: 6.25,
    weightLb: 3.7, drawWeightRange: [40, 60], drawLengthRange: [22, 29],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Reckoning Gen2 SD', year: 2024, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6.5,
    weightLb: 4.5, drawWeightRange: [50, 70], drawLengthRange: [26, 30],
    titaniumKitVariantId: 47844947230946, titaniumKitPrice: '154.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2024-reckoning',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Solution SS', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Commander', year: 2024, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [50, 70], drawLengthRange: [26, 30.5],
    titaniumKitVariantId: 47844947165410, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2024-commander',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Core SS', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947198178, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2024-core-ss',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Proven 34', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 34, brace: 7.0,
    weightLb: 4.5, drawWeightRange: [50, 70], drawLengthRange: [27.5, 31.5],
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Core SS', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947296482, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2025-core-ss-sr-proven-honor-solution',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'SR', year: 2025, iboSpeed: 350, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [50, 70], drawLengthRange: [26, 30],
    titaniumKitVariantId: 47844947296482, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2025-core-ss-sr-proven-honor-solution',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Proven', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947296482, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2025-core-ss-sr-proven-honor-solution',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Honor', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947296482, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2025-core-ss-sr-proven-honor-solution',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Solution', year: 2025, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6.0,
    weightLb: 4.3, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947296482, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2025-core-ss-sr-proven-honor-solution',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Reckoning', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 6.5,
    weightLb: 4.5, drawWeightRange: [50, 70], drawLengthRange: [26, 30],
    titaniumKitVariantId: 47844947362018, titaniumKitPrice: '139.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-bowtech-2020-21-reckoning',
    tuning: 'DeadLock' },

  // ─────────────────────────────────────────────────────────────────
  // ELITE — SimplexPRO + SET tuning across EnVision, Era, Omnia, Carbon Eon, Exalt, Ethos, Varos
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Elite', model: 'EnVision', year: 2023, iboSpeed: 334, letOff: 75, axleToAxle: 31, brace: 6.875,
    weightLb: 4.45, drawWeightRange: [40, 75], drawLengthRange: [23.5, 30.5],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Era', year: 2024, iboSpeed: 335, letOff: 75, axleToAxle: 30.5, brace: 6.5,
    weightLb: 4.0, drawWeightRange: [40, 70], drawLengthRange: [25, 30],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Omnia', year: 2025, iboSpeed: 347, letOff: 75, axleToAxle: 31, brace: 6.0,
    weightLb: 4.4, drawWeightRange: [40, 75], drawLengthRange: [25.5, 30.5],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Ethos', year: 2025, iboSpeed: 333, letOff: 75, axleToAxle: 33, brace: 7.0,
    weightLb: 4.5, drawWeightRange: [40, 70], drawLengthRange: [26, 31],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Exalt', year: 2025, iboSpeed: 333, letOff: 75, axleToAxle: 32, brace: 6.5,
    weightLb: 4.5, drawWeightRange: [40, 70], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947656930, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-elite-2025-exalt',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Exalt 35', year: 2025, iboSpeed: 330, letOff: 75, axleToAxle: 35, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [40, 70], drawLengthRange: [27, 32],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Carbon Eon', year: 2025, iboSpeed: 336, letOff: 75, axleToAxle: 31.3125, brace: 7.0625,
    weightLb: 4.0, drawWeightRange: [40, 70], drawLengthRange: [25.5, 31],
    titaniumKitVariantId: 47844947591394, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-elite-2025-carbon-eon-2026-veros',
    tuning: 'S.E.T.' },
  /* Elite 2026 lineup — Varos (32 ATA flagship), Artus (compact 30 ATA),
   * Terrain (forgiving 7" brace), plus the Emerse youth bow. SP2 Cam System
   * + VX Mods + S.E.T. Technology. Let-off adjustable up to 90%.
   * Specs verified June 2026 against elitearchery.com. */
  { brand: 'Elite', model: 'Varos', year: 2026, iboSpeed: 338, letOff: 90, axleToAxle: 32, brace: 6.625,
    weightLb: 4.75, drawWeightRange: [40, 75], drawLengthRange: [25.5, 31],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Artus', year: 2026, iboSpeed: 340, letOff: 90, axleToAxle: 30, brace: 6.875,
    weightLb: 4.7, drawWeightRange: [40, 75], drawLengthRange: [25, 30.5],
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Terrain', year: 2026, iboSpeed: 325, letOff: 80, axleToAxle: 31, brace: 7.0,
    weightLb: 4.6, drawWeightRange: [40, 70], drawLengthRange: [25, 30.5],
    tuning: 'S.E.T.' },
  /* NOTE: previous catalog had "Elite Veros 2026" which is not a real model
   * in Elite's 2026 lineup. The Firenock titanium-kit handle is preserved on
   * the Carbon EON (2025) line where it was originally targeted. */

  // ─────────────────────────────────────────────────────────────────
  // BEAR — Refine EKO (2022) → Whitetail Legend (2022-25) → Persist / Legend 30 / Whitetail INT (2025) → Adapt+ (2026)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Bear', model: 'Refine EKO', year: 2022, iboSpeed: 340, letOff: 75, axleToAxle: 33, brace: 6.375,
    weightLb: 4.3, drawWeightRange: [45, 70], drawLengthRange: [25.5, 30] },
  { brand: 'Bear', model: 'Whitetail Legend Pro', year: 2022, iboSpeed: 335, letOff: 80, axleToAxle: 30, brace: 6.25,
    weightLb: 4.0, drawWeightRange: [45, 70], drawLengthRange: [25, 30] },
  { brand: 'Bear', model: 'Whitetail Legend', year: 2023, iboSpeed: 320, letOff: 80, axleToAxle: 30, brace: 6.25,
    weightLb: 4.0, drawWeightRange: [45, 70], drawLengthRange: [25, 30] },
  { brand: 'Bear', model: 'Alaskan', year: 2024, iboSpeed: 320, letOff: 80, axleToAxle: 32, brace: 6.5,
    weightLb: 4.4, drawWeightRange: [50, 70], drawLengthRange: [26.5, 30.5] },
  { brand: 'Bear', model: 'Whitetail Maxx', year: 2024, iboSpeed: 320, letOff: 80, axleToAxle: 30.5, brace: 6.25,
    weightLb: 4.1, drawWeightRange: [45, 70], drawLengthRange: [25.5, 30] },
  { brand: 'Bear', model: 'Persist 33', year: 2025, iboSpeed: 335, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.2, drawWeightRange: [45, 70], drawLengthRange: [26, 31] },
  { brand: 'Bear', model: 'Legend 30', year: 2025, iboSpeed: 330, letOff: 80, axleToAxle: 30, brace: 6.0,
    weightLb: 4.0, drawWeightRange: [45, 70], drawLengthRange: [25, 30] },
  { brand: 'Bear', model: 'Whitetail INT', year: 2025, iboSpeed: 330, letOff: 80, axleToAxle: 30.5, brace: 6.25,
    weightLb: 4.1, drawWeightRange: [45, 70], drawLengthRange: [25.5, 30] },
  { brand: 'Bear', model: 'Adapt+', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.25,
    weightLb: 4.1, drawWeightRange: [45, 75], drawLengthRange: [25, 30.5] },

  // ─────────────────────────────────────────────────────────────────
  // XPEDITION ARCHERY — 2026 NexLite series. Proprietary Magnite alloy riser.
  // Specs verified against xpeditionarchery.com June 2026.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Xpedition', model: 'XLite 29', year: 2026, iboSpeed: 333, letOff: 80, axleToAxle: 29, brace: 6.0,
    weightLb: 3.65, drawWeightRange: [50, 80], drawLengthRange: [24, 29] },
  { brand: 'Xpedition', model: 'XLite 31', year: 2026, iboSpeed: 336, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 3.75, drawWeightRange: [50, 70], drawLengthRange: [25.5, 30.5] },
  { brand: 'Xpedition', model: 'XLite 32', year: 2026, iboSpeed: 342, letOff: 80, axleToAxle: 32, brace: 6.0,
    weightLb: 3.8, drawWeightRange: [50, 80], drawLengthRange: [25, 30.5] },
  { brand: 'Xpedition', model: 'XLite 33', year: 2026, iboSpeed: 330, letOff: 85, axleToAxle: 33, brace: 6.375,
    weightLb: 3.75, drawWeightRange: [50, 80], drawLengthRange: [24.5, 31] },
  { brand: 'Xpedition', model: 'XLite 35', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 35, brace: 6.0,
    weightLb: 3.9, drawWeightRange: [50, 80], drawLengthRange: [26, 31] },
  { brand: 'Xpedition', model: 'MX-32', year: 2026, iboSpeed: 347, letOff: 80, axleToAxle: 32.5, brace: 6.5,
    weightLb: 4.4, drawWeightRange: [40, 70], drawLengthRange: [26.5, 31] },

  // ─────────────────────────────────────────────────────────────────
  // APA ARCHERY — 2026 hunting lineup. X1/X2 dual cam, toolless tuning, custom finishes.
  // Specs verified against apaarchery.com June 2026.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'APA', model: 'Black Mamba Pro 6', year: 2026, iboSpeed: 357, letOff: 80, axleToAxle: 31.375, brace: 6.0625,
    weightLb: 3.9, drawWeightRange: [40, 80], drawLengthRange: [24, 30] },
  { brand: 'APA', model: 'King Cobra DGX', year: 2026, iboSpeed: 363, letOff: 80, axleToAxle: 32.25, brace: 5.375,
    weightLb: 4.1, drawWeightRange: [40, 100], drawLengthRange: [24, 30] },
  { brand: 'APA', model: 'Black Mamba X33', year: 2026, iboSpeed: 347, letOff: 80, axleToAxle: 33, brace: 6.3125,
    weightLb: 3.9, drawWeightRange: [40, 100], drawLengthRange: [25.5, 31.5] },

  // ─────────────────────────────────────────────────────────────────
  // TRUTH ARCHERY — Kevin Strother's magnesium platform. KSET cam adjustment.
  // Specs verified against trutharchery.com June 2026.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Truth Archery', model: 'Truth 31 MG', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 31.1875, brace: 6.5,
    weightLb: 3.4, drawWeightRange: [40, 70], drawLengthRange: [25, 30.5] },
  { brand: 'Truth Archery', model: 'Truth 33 MG', year: 2026, iboSpeed: 342, letOff: 80, axleToAxle: 33.25, brace: 6.5,
    weightLb: 3.45, drawWeightRange: [40, 70], drawLengthRange: [25.5, 31] },
  { brand: 'Truth Archery', model: 'Truth 35 MG', year: 2026, iboSpeed: 332, letOff: 80, axleToAxle: 35, brace: 6.5,
    weightLb: 3.55, drawWeightRange: [40, 70], drawLengthRange: [26.5, 32] },

  // ─────────────────────────────────────────────────────────────────
  // MISSION ARCHERY — Mathews subsidiary. Entry-level / adjustable platform —
  // youth, new hunters, and tunable family bows. Specs verified against
  // missionarchery.com June 2026.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Mission', model: 'Menace XR', year: 2026, iboSpeed: 295, letOff: 80, axleToAxle: 28, brace: 6.5,
    weightLb: 2.81, drawWeightRange: [10, 50], drawLengthRange: [17, 28] },
  { brand: 'Mission', model: 'Hammr', year: 2026, iboSpeed: 305, letOff: 80, axleToAxle: 28, brace: 6.5,
    weightLb: 3.4, drawWeightRange: [16, 70], drawLengthRange: [17, 29] },
  { brand: 'Mission', model: 'Switch', year: 2026, iboSpeed: 310, letOff: 80, axleToAxle: 30, brace: 7.0,
    weightLb: 3.85, drawWeightRange: [10, 70], drawLengthRange: [18, 30] },
  { brand: 'Mission', model: 'Range', year: 2026, iboSpeed: 325, letOff: 80, axleToAxle: 31, brace: 7.0,
    weightLb: 4.06, drawWeightRange: [30, 70], drawLengthRange: [22, 30] },
  // 2026 Stryde — new solocam value platform (replaces Switch). Standard limbs
  // 50-70 lb; Stryde Lite limbs (30-50 lb) are a separate limb option, not a
  // separate bow. Specs: missionarchery.com / Outdoor Life + Bowhunting.com June 2026.
  { brand: 'Mission', model: 'Stryde 28', year: 2026, iboSpeed: 320, letOff: 80, axleToAxle: 28, brace: 6.0,
    weightLb: 3.8, drawWeightRange: [50, 70], drawLengthRange: [21, 29] },
  { brand: 'Mission', model: 'Stryde 30.5', year: 2026, iboSpeed: 323, letOff: 80, axleToAxle: 30.5, brace: 6.5,
    weightLb: 4.0, drawWeightRange: [50, 70], drawLengthRange: [22, 30] },

  // ─────────────────────────────────────────────────────────────────
  // PRIME — InLine 1/3/5 (2022) → Revex 2/4/6 (2024-25)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Prime', model: 'InLine 1', year: 2022, iboSpeed: 332, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 4.5, drawWeightRange: [40, 80], drawLengthRange: [25, 30],
    titaniumKitVariantId: 47844947755234, titaniumKitPrice: '129.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-prime-2022-inline-1-3-5',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'InLine 3', year: 2022, iboSpeed: 332, letOff: 80, axleToAxle: 33, brace: 6.5,
    weightLb: 4.6, drawWeightRange: [40, 80], drawLengthRange: [26, 30.5],
    titaniumKitVariantId: 47844947755234, titaniumKitPrice: '129.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-prime-2022-inline-1-3-5',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'InLine 5', year: 2022, iboSpeed: 330, letOff: 80, axleToAxle: 35, brace: 7.0,
    weightLb: 4.8, drawWeightRange: [40, 80], drawLengthRange: [27, 32],
    titaniumKitVariantId: 47844947755234, titaniumKitPrice: '129.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-prime-2022-inline-1-3-5',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Revex 2', year: 2024, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 6.25,
    weightLb: 4.4, drawWeightRange: [40, 80], drawLengthRange: [26, 31.5],
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Revex 4', year: 2024, iboSpeed: 332, letOff: 80, axleToAxle: 34, brace: 6.75,
    weightLb: 4.6, drawWeightRange: [40, 80], drawLengthRange: [27, 32],
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Revex 6', year: 2024, iboSpeed: 328, letOff: 80, axleToAxle: 36, brace: 7.25,
    weightLb: 4.8, drawWeightRange: [40, 80], drawLengthRange: [28, 32.5],
    tuning: 'center-track cams' },

  // ─────────────────────────────────────────────────────────────────
  // DARTON — Sequel (2024-25) → TriTech (2026)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Darton', model: 'Sequel 31', year: 2024, iboSpeed: 345, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 4.2, drawWeightRange: [45, 80], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947394786, titaniumKitPrice: '164.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2024-sequel-31-33' },
  { brand: 'Darton', model: 'Sequel 33', year: 2024, iboSpeed: 343, letOff: 80, axleToAxle: 33, brace: 7.0,
    weightLb: 4.4, drawWeightRange: [45, 80], drawLengthRange: [26.5, 31.5],
    titaniumKitVariantId: 47844947394786, titaniumKitPrice: '164.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2024-sequel-31-33' },
  { brand: 'Darton', model: 'Sequel', year: 2025, iboSpeed: 345, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 4.2, drawWeightRange: [45, 80], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947525858, titaniumKitPrice: '149.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2025-sequel' },
  { brand: 'Darton', model: 'TriTech 31', year: 2026, iboSpeed: 345, letOff: 80, axleToAxle: 31, brace: 6.5,
    weightLb: 4.2, drawWeightRange: [45, 80], drawLengthRange: [25.5, 30.5],
    titaniumKitVariantId: 47844947558626, titaniumKitPrice: '184.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2026-tritech-31-33-35' },
  { brand: 'Darton', model: 'TriTech 33', year: 2026, iboSpeed: 343, letOff: 80, axleToAxle: 33, brace: 7.0,
    weightLb: 4.4, drawWeightRange: [45, 80], drawLengthRange: [26.5, 31.5],
    titaniumKitVariantId: 47844947558626, titaniumKitPrice: '184.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2026-tritech-31-33-35' },
  { brand: 'Darton', model: 'TriTech 35', year: 2026, iboSpeed: 340, letOff: 80, axleToAxle: 35, brace: 7.0,
    weightLb: 4.6, drawWeightRange: [45, 80], drawLengthRange: [27.5, 32],
    titaniumKitVariantId: 47844947558626, titaniumKitPrice: '184.95',
    titaniumKitProductHandle: 'firenock-titanium-kit-darton-2026-tritech-31-33-35' },

  // ═════════════════════════════════════════════════════════════════════════
  // TARGET COMPOUND BOWS — Indoor / Outdoor / Field / Vegas / NFAA / WA / JOAD
  // ═════════════════════════════════════════════════════════════════════════
  // Long ATA (34-40"), longer brace, smoother draw, low let-off available.
  // 4 years deep — most target archers upgrade every 2-3 years and tuning specs
  // are critical for compete tab equipment-compliance checking.
  //
  // Verified May 2026 from: mathewsinc.com, hoyt.com, hoyttarget.com,
  // psearchery.com, bowtecharchery.com, elitearchery.com, g5prime.com,
  // lancasterarchery.com spec sheets, bow-international.com reviews.
  // ═════════════════════════════════════════════════════════════════════════

  // ─────────────────────────────────────────────────────────────────
  // MATHEWS TARGET — TRX (Crosscentric cam, 2022-24) → TITLE (SWT cam, 2025-26)
  // TRX comes in 34/36/38/40 ATA. TITLE replaced TRX for 2025+ as new flagship.
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Mathews', model: 'TRX 34', year: 2022, iboSpeed: 327, letOff: 65, axleToAxle: 34, brace: 7.5,
    weightLb: 5.45, drawWeightRange: [40, 70], drawLengthRange: [26, 31], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 36', year: 2022, iboSpeed: 324, letOff: 65, axleToAxle: 36, brace: 7.5,
    weightLb: 5.55, drawWeightRange: [40, 70], drawLengthRange: [27, 32], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38 G2', year: 2023, iboSpeed: 320, letOff: 65, axleToAxle: 38, brace: 7.5,
    weightLb: 5.65, drawWeightRange: [40, 60], drawLengthRange: [28, 32.5], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 40 G2', year: 2023, iboSpeed: 314, letOff: 65, axleToAxle: 40, brace: 7.5,
    weightLb: 5.75, drawWeightRange: [40, 60], drawLengthRange: [29, 33], category: 'target',
    tuning: 'top-hat spacers' },
  // TITLE — new SWT (Switch Weight Technology) cam, replaced TRX 2025
  { brand: 'Mathews', model: 'TITLE 34', year: 2025, iboSpeed: 330, letOff: 65, axleToAxle: 34, brace: 7.5,
    weightLb: 5.4, drawWeightRange: [40, 70], drawLengthRange: [26, 31], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 36', year: 2025, iboSpeed: 327, letOff: 65, axleToAxle: 36, brace: 7.5,
    weightLb: 5.5, drawWeightRange: [40, 70], drawLengthRange: [27, 32], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 38', year: 2025, iboSpeed: 323, letOff: 65, axleToAxle: 38, brace: 7.5,
    weightLb: 5.6, drawWeightRange: [40, 60], drawLengthRange: [28, 32.5], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 40', year: 2025, iboSpeed: 317, letOff: 65, axleToAxle: 40, brace: 7.5,
    weightLb: 5.7, drawWeightRange: [40, 60], drawLengthRange: [29, 33], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 34', year: 2026, iboSpeed: 332, letOff: 65, axleToAxle: 34, brace: 7.5,
    weightLb: 5.4, drawWeightRange: [40, 70], drawLengthRange: [26, 31], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 36', year: 2026, iboSpeed: 329, letOff: 65, axleToAxle: 36, brace: 7.5,
    weightLb: 5.5, drawWeightRange: [40, 70], drawLengthRange: [27, 32], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 38', year: 2026, iboSpeed: 325, letOff: 65, axleToAxle: 38, brace: 7.5,
    weightLb: 5.6, drawWeightRange: [40, 60], drawLengthRange: [28, 32.5], category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TITLE 40', year: 2026, iboSpeed: 319, letOff: 65, axleToAxle: 40, brace: 7.5,
    weightLb: 5.7, drawWeightRange: [40, 60], drawLengthRange: [29, 33], category: 'target',
    tuning: 'top-hat spacers' },

  // ─────────────────────────────────────────────────────────────────
  // HOYT TARGET — Pro Force / Pro Comp Elite FX (2022) → Concept FX / Concept X 37 / Concept X 40 (2024-26)
  // Concept series is new flagship from 2024 onward with SCTR cam (Cam & 1/2 + HB hybrid).
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Hoyt', model: 'Pro Force FX', year: 2022, iboSpeed: 332, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.6, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'Hoyt', model: 'Pro Comp Elite FX', year: 2022, iboSpeed: 325, letOff: 65, axleToAxle: 40, brace: 7.625,
    weightLb: 4.85, drawWeightRange: [30, 60], drawLengthRange: [25.5, 31.5], category: 'target' },
  // 2024 Concept series launch
  { brand: 'Hoyt', model: 'Concept FX 34', year: 2024, iboSpeed: 332, letOff: 65, axleToAxle: 34, brace: 6.25,
    weightLb: 4.5, drawWeightRange: [30, 60], drawLengthRange: [24.5, 30], category: 'target' },
  { brand: 'Hoyt', model: 'Concept X 37', year: 2024, iboSpeed: 330, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.75, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'Hoyt', model: 'Concept X 40', year: 2024, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  // 2025 / 2026 carry-forward (SCTR cam unchanged, refresh-year variants)
  { brand: 'Hoyt', model: 'Concept X 37', year: 2025, iboSpeed: 330, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.75, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'Hoyt', model: 'Concept X 40', year: 2025, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  { brand: 'Hoyt', model: 'Concept X 37', year: 2026, iboSpeed: 332, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.75, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'Hoyt', model: 'Concept X 40', year: 2026, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  { brand: 'Hoyt', model: 'Concept FX', year: 2026, iboSpeed: 332, letOff: 65, axleToAxle: 34, brace: 6.25,
    weightLb: 4.5, drawWeightRange: [30, 60], drawLengthRange: [24.5, 30], category: 'target' },

  // ─────────────────────────────────────────────────────────────────
  // PSE TARGET — Supra (long-running target) + Citation 40 (2024-26)
  // Supra Focus XL (2022-23) → Supra EXT (2024-25) → Citation 40 (2026)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'PSE', model: 'Supra Focus XL', year: 2022, iboSpeed: 330, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'PSE', model: 'Supra EXT 33', year: 2023, iboSpeed: 332, letOff: 65, axleToAxle: 33, brace: 6.5,
    weightLb: 4.5, drawWeightRange: [30, 60], drawLengthRange: [24, 30], category: 'target' },
  { brand: 'PSE', model: 'Supra EXT 37', year: 2023, iboSpeed: 330, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },
  { brand: 'PSE', model: 'Supra EXT 40', year: 2024, iboSpeed: 326, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  { brand: 'PSE', model: 'Citation 40', year: 2025, iboSpeed: 328, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  { brand: 'PSE', model: 'Citation 40', year: 2026, iboSpeed: 330, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.9, drawWeightRange: [30, 60], drawLengthRange: [26, 32], category: 'target' },
  { brand: 'PSE', model: 'Supra X 37', year: 2026, iboSpeed: 332, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [30, 60], drawLengthRange: [25, 31], category: 'target' },

  // ─────────────────────────────────────────────────────────────────
  // BOWTECH TARGET — Specialist II (2022-24) → Patriot (2025) → Reign Target (2026)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Bowtech', model: 'Specialist II 38', year: 2022, iboSpeed: 325, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 31], category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Specialist II 40', year: 2022, iboSpeed: 322, letOff: 65, axleToAxle: 40, brace: 7.0,
    weightLb: 5.0, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Patriot 38', year: 2025, iboSpeed: 328, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 31], category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Patriot 40', year: 2025, iboSpeed: 325, letOff: 65, axleToAxle: 40, brace: 7.0,
    weightLb: 5.0, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'DeadLock' },
  // 2026 Alliance is Bowtech's new flagship — also offered in target trims (30/33 ATA hunter-leaning)
  // True dedicated target stays Patriot 38/40 in 2026.
  { brand: 'Bowtech', model: 'Patriot 38', year: 2026, iboSpeed: 330, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 31], category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Patriot 40', year: 2026, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.0,
    weightLb: 5.0, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'DeadLock' },

  // ─────────────────────────────────────────────────────────────────
  // ELITE TARGET — Victory 37 (2022-24) → Verdict (2025-26)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Elite', model: 'Victory 37', year: 2022, iboSpeed: 325, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [40, 60], drawLengthRange: [25.5, 31], category: 'target',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Victory 40', year: 2023, iboSpeed: 322, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 32], category: 'target',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Verdict 37', year: 2025, iboSpeed: 328, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [40, 60], drawLengthRange: [25.5, 31], category: 'target',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Verdict 40', year: 2025, iboSpeed: 325, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 32], category: 'target',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Verdict 37', year: 2026, iboSpeed: 330, letOff: 65, axleToAxle: 37, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [40, 60], drawLengthRange: [25.5, 31], category: 'target',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Verdict 40', year: 2026, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 32], category: 'target',
    tuning: 'S.E.T.' },

  // ─────────────────────────────────────────────────────────────────
  // PRIME TARGET — Logic CT3/CT5 (2022) → Nexus 2/4 (2023-24) → Form (2025-26)
  // ─────────────────────────────────────────────────────────────────
  { brand: 'Prime', model: 'Logic CT3', year: 2022, iboSpeed: 328, letOff: 65, axleToAxle: 36, brace: 7.0,
    weightLb: 4.7, drawWeightRange: [40, 60], drawLengthRange: [25.5, 31], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Logic CT5', year: 2022, iboSpeed: 322, letOff: 65, axleToAxle: 39, brace: 7.5,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Nexus 2', year: 2023, iboSpeed: 332, letOff: 65, axleToAxle: 36, brace: 6.75,
    weightLb: 4.65, drawWeightRange: [40, 60], drawLengthRange: [25, 31], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Nexus 4', year: 2023, iboSpeed: 328, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.85, drawWeightRange: [40, 60], drawLengthRange: [26, 32], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form 38', year: 2025, iboSpeed: 330, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.8, drawWeightRange: [40, 60], drawLengthRange: [26, 31.5], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form 40', year: 2025, iboSpeed: 325, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 5.0, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form 38', year: 2026, iboSpeed: 332, letOff: 65, axleToAxle: 38, brace: 7.0,
    weightLb: 4.8, drawWeightRange: [40, 60], drawLengthRange: [26, 31.5], category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form 40', year: 2026, iboSpeed: 327, letOff: 65, axleToAxle: 40, brace: 7.25,
    weightLb: 5.0, drawWeightRange: [40, 60], drawLengthRange: [27, 32], category: 'target',
    tuning: 'center-track cams' },

  // ════════════════════════════════════════════════════════════════════
  // EXPANDED 2016-2026 CATALOG — added 2026-06-01 v104
  // 272 entries researched + deduped against existing flagship coverage.
  // Brands: Mathews, Hoyt, Bowtech, Elite, Prime, PSE, Bear, BlackOut,
  // Mission, Diamond, Xpedition, APA. Hunting + target.
  // ════════════════════════════════════════════════════════════════════
  // ─────────────────────────────────────────────────────────────────────
  // MATHEWS — flagships, sub-flagships, target
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Mathews', model: 'Halon 32', year: 2016, iboSpeed: 350, letOff: 85, axleToAxle: 32, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Halon 6', year: 2016, iboSpeed: 343, letOff: 85, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Halon 7', year: 2016, iboSpeed: 333, letOff: 85, axleToAxle: 30, brace: 7, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Halon X Comp', year: 2017, iboSpeed: 343, letOff: 75, axleToAxle: 35, brace: 7, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Avail', year: 2017, iboSpeed: 332, letOff: 85, axleToAxle: 30, brace: 7, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38', year: 2017, iboSpeed: 318, letOff: 75, axleToAxle: 38, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 40', year: 2018, iboSpeed: 315, letOff: 75, axleToAxle: 40, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Triax', year: 2018, iboSpeed: 343, letOff: 85, axleToAxle: 28, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Stoke', year: 2018, iboSpeed: 295, letOff: 80, axleToAxle: 28, brace: 7, category: 'hunting' },
  { brand: 'Mathews', model: 'Vertix', year: 2019, iboSpeed: 343, letOff: 85, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Traverse', year: 2019, iboSpeed: 350, letOff: 85, axleToAxle: 33, brace: 5.5, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 36', year: 2019, iboSpeed: 318, letOff: 75, axleToAxle: 36, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38 G2', year: 2020, iboSpeed: 318, letOff: 75, axleToAxle: 38, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'VXR 28', year: 2020, iboSpeed: 344, letOff: 85, axleToAxle: 28, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'VXR 31.5', year: 2020, iboSpeed: 343, letOff: 85, axleToAxle: 31.5, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Atlas', year: 2020, iboSpeed: 339, letOff: 85, axleToAxle: 34, brace: 6.5, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Prima', year: 2021, iboSpeed: 320, letOff: 85, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'V3 27', year: 2021, iboSpeed: 342, letOff: 85, axleToAxle: 27, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'V3 31', year: 2021, iboSpeed: 336, letOff: 85, axleToAxle: 31, brace: 6, category: 'hunting',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38 G3', year: 2021, iboSpeed: 318, letOff: 75, axleToAxle: 38, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'Image', year: 2022, iboSpeed: 295, letOff: 80, axleToAxle: 30, brace: 7, category: 'hunting' },
  { brand: 'Mathews', model: 'TRX 40', year: 2022, iboSpeed: 315, letOff: 75, axleToAxle: 40, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38 G4', year: 2023, iboSpeed: 318, letOff: 75, axleToAxle: 38, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },
  { brand: 'Mathews', model: 'TRX 38 G5', year: 2025, iboSpeed: 318, letOff: 75, axleToAxle: 38, brace: 7.5, category: 'target',
    tuning: 'top-hat spacers' },

  // ─────────────────────────────────────────────────────────────────────
  // HOYT — flagships, sub-flagships, target
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Hoyt', model: 'Defiant', year: 2016, iboSpeed: 331, letOff: 80, axleToAxle: 30.5, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Defiant 34', year: 2016, iboSpeed: 327, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Defiant Turbo', year: 2016, iboSpeed: 342, letOff: 80, axleToAxle: 30.5, brace: 6, category: 'hunting' },
  { brand: 'Hoyt', model: 'Carbon Defiant', year: 2016, iboSpeed: 331, letOff: 80, axleToAxle: 30.5, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Carbon Defiant 34', year: 2016, iboSpeed: 325, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Carbon Defiant Turbo', year: 2016, iboSpeed: 342, letOff: 80, axleToAxle: 30.5, brace: 6, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Defiant', year: 2017, iboSpeed: 331, letOff: 80, axleToAxle: 30.5, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Defiant 34', year: 2017, iboSpeed: 327, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Defiant Turbo', year: 2017, iboSpeed: 340, letOff: 80, axleToAxle: 30.5, brace: 6, category: 'hunting' },
  { brand: 'Hoyt', model: 'Powermax', year: 2017, iboSpeed: 328, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Comp Elite FX', year: 2016, iboSpeed: 322, letOff: 75, axleToAxle: 39, brace: 7, category: 'target' },
  { brand: 'Hoyt', model: 'Podium 37', year: 2016, iboSpeed: 326, letOff: 75, axleToAxle: 37, brace: 7, category: 'target' },
  { brand: 'Hoyt', model: 'Podium X Elite 40', year: 2017, iboSpeed: 320, letOff: 75, axleToAxle: 40, brace: 7, category: 'target' },
  { brand: 'Hoyt', model: 'Hyperforce', year: 2018, iboSpeed: 330, letOff: 80, axleToAxle: 30.5, brace: 6.75, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Force', year: 2018, iboSpeed: 335, letOff: 80, axleToAxle: 30.5, brace: 6.75, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-1', year: 2018, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-1 Turbo', year: 2018, iboSpeed: 350, letOff: 80, axleToAxle: 32, brace: 5.875, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-1 Ultra', year: 2018, iboSpeed: 330, letOff: 80, axleToAxle: 35, brace: 6, category: 'hunting' },
  { brand: 'Hoyt', model: 'Pro Comp Elite XT', year: 2018, iboSpeed: 322, letOff: 75, axleToAxle: 39, brace: 7, category: 'target' },
  { brand: 'Hoyt', model: 'Helix', year: 2019, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'Hoyt', model: 'Helix Turbo', year: 2019, iboSpeed: 350, letOff: 80, axleToAxle: 31, brace: 5.875, category: 'hunting' },
  { brand: 'Hoyt', model: 'Helix Ultra', year: 2019, iboSpeed: 334, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-3', year: 2019, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-3 Turbo', year: 2019, iboSpeed: 350, letOff: 80, axleToAxle: 31, brace: 5.875, category: 'hunting' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-3 Ultra', year: 2019, iboSpeed: 334, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Hoyt', model: 'Axius Alpha', year: 2020, iboSpeed: 342, letOff: 80, axleToAxle: 29.5, brace: 6.125, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Axius Ultra', year: 2020, iboSpeed: 334, letOff: 80, axleToAxle: 33.5, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-4 Alpha', year: 2020, iboSpeed: 342, letOff: 80, axleToAxle: 29.5, brace: 6.125, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-4 Turbo', year: 2020, iboSpeed: 350, letOff: 80, axleToAxle: 30.5, brace: 5.875, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'REDWRX Carbon RX-4 Ultra', year: 2020, iboSpeed: 334, letOff: 80, axleToAxle: 33.5, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Ventum 30', year: 2021, iboSpeed: 334, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Ventum 33', year: 2021, iboSpeed: 334, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-5', year: 2021, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-5 Ultra', year: 2021, iboSpeed: 334, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Pro Force FX', year: 2021, iboSpeed: 322, letOff: 75, axleToAxle: 36, brace: 7, category: 'target' },
  { brand: 'Hoyt', model: 'Ventum Pro 30', year: 2022, iboSpeed: 334, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Ventum Pro 33', year: 2022, iboSpeed: 334, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-7', year: 2022, iboSpeed: 342, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-7 Ultra', year: 2022, iboSpeed: 334, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-8', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.125, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Carbon RX-8 Ultra', year: 2023, iboSpeed: 334, letOff: 80, axleToAxle: 34, brace: 6.75, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'Alpha AX-2 32', year: 2025, iboSpeed: 336, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting',
    tuning: 'HBX cam spacers' },
  { brand: 'Hoyt', model: 'AX-3', year: 2026, iboSpeed: 344, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'XTS wedge' },
  { brand: 'Hoyt', model: 'Pro Comp X', year: 2026, iboSpeed: 322, letOff: 75, axleToAxle: 38, brace: 7, category: 'target' },

  // ─────────────────────────────────────────────────────────────────────
  // BOWTECH — flagships + target
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Bowtech', model: 'Carbon Knight', year: 2016, iboSpeed: 335, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'BTX 31', year: 2016, iboSpeed: 335, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'BTX 28', year: 2016, iboSpeed: 343, letOff: 80, axleToAxle: 28, brace: 6, category: 'hunting' },
  { brand: 'Bowtech', model: 'Reign 6', year: 2017, iboSpeed: 350, letOff: 80, axleToAxle: 31, brace: 6, category: 'hunting' },
  { brand: 'Bowtech', model: 'Reign 7', year: 2017, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'Boss', year: 2017, iboSpeed: 320, letOff: 80, axleToAxle: 35, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'Realm', year: 2018, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Bowtech', model: 'Realm X', year: 2018, iboSpeed: 335, letOff: 80, axleToAxle: 35, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'Realm SR6', year: 2019, iboSpeed: 352, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bowtech', model: 'Realm SS', year: 2019, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Bowtech', model: 'Revolt', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Revolt X', year: 2020, iboSpeed: 340, letOff: 80, axleToAxle: 33, brace: 7, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Amplify', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 31.5, brace: 6.625, category: 'hunting' },
  { brand: 'Bowtech', model: 'Solution', year: 2021, iboSpeed: 352, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Solution SS', year: 2021, iboSpeed: 343, letOff: 80, axleToAxle: 33, brace: 7, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Solution SD', year: 2021, iboSpeed: 343, letOff: 80, axleToAxle: 28, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Specialist II', year: 2021, iboSpeed: 318, letOff: 75, axleToAxle: 39, brace: 7.625, category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'SR350', year: 2022, iboSpeed: 350, letOff: 80, axleToAxle: 31.5, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Carbon One', year: 2022, iboSpeed: 343, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Reckoning 35', year: 2022, iboSpeed: 331, letOff: 75, axleToAxle: 35, brace: 7, category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Reckoning 38', year: 2022, iboSpeed: 326, letOff: 75, axleToAxle: 38, brace: 7, category: 'target',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'CP28', year: 2023, iboSpeed: 339, letOff: 80, axleToAxle: 28, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'CP30', year: 2023, iboSpeed: 331, letOff: 80, axleToAxle: 30, brace: 6.75, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Core SR', year: 2024, iboSpeed: 343, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Core SD', year: 2024, iboSpeed: 339, letOff: 80, axleToAxle: 28, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Honor SS', year: 2025, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Alliance', year: 2026, iboSpeed: 345, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'DeadLock' },
  { brand: 'Bowtech', model: 'Alliance SS', year: 2026, iboSpeed: 337, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting',
    tuning: 'DeadLock' },

  // ─────────────────────────────────────────────────────────────────────
  // ELITE
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Elite', model: 'Energy 32', year: 2016, iboSpeed: 332, letOff: 75, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Energy 35', year: 2016, iboSpeed: 335, letOff: 75, axleToAxle: 35, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Impulse 31', year: 2016, iboSpeed: 335, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Impulse 34', year: 2016, iboSpeed: 332, letOff: 75, axleToAxle: 34, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Option 6', year: 2017, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Elite', model: 'Option 7', year: 2017, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Ritual 33', year: 2018, iboSpeed: 335, letOff: 75, axleToAxle: 33, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Ritual 35', year: 2018, iboSpeed: 332, letOff: 75, axleToAxle: 35, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Echelon 37', year: 2018, iboSpeed: 320, letOff: 75, axleToAxle: 37, brace: 7, category: 'target' },
  { brand: 'Elite', model: 'Echelon 39', year: 2018, iboSpeed: 318, letOff: 75, axleToAxle: 39, brace: 7, category: 'target' },
  { brand: 'Elite', model: 'Kure', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'Synergy', year: 2020, iboSpeed: 325, letOff: 80, axleToAxle: 33.5, brace: 7.375, category: 'hunting' },
  { brand: 'Elite', model: 'EnVision', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Elite', model: 'EnKore', year: 2021, iboSpeed: 340, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Remedy', year: 2021, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Verdict', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 32.5, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Omnia', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Era', year: 2023, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Terrain', year: 2023, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Ethos', year: 2024, iboSpeed: 340, letOff: 90, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Allure', year: 2024, iboSpeed: 318, letOff: 80, axleToAxle: 30, brace: 6.75, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'HNTR', year: 2025, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'HNTR XL', year: 2025, iboSpeed: 336, letOff: 80, axleToAxle: 33, brace: 7, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Exalt', year: 2026, iboSpeed: 342, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting',
    tuning: 'S.E.T.' },
  { brand: 'Elite', model: 'Exalt XL', year: 2026, iboSpeed: 338, letOff: 80, axleToAxle: 33, brace: 7, category: 'hunting',
    tuning: 'S.E.T.' },

  // ─────────────────────────────────────────────────────────────────────
  // PRIME (G5)
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Prime', model: 'Centergy', year: 2017, iboSpeed: 333, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Centergy Air', year: 2017, iboSpeed: 330, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Centergy Hybrid', year: 2018, iboSpeed: 333, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Logic CT3', year: 2018, iboSpeed: 332, letOff: 75, axleToAxle: 33, brace: 7, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Logic CT5', year: 2018, iboSpeed: 326, letOff: 75, axleToAxle: 35, brace: 7, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Logic CT9', year: 2018, iboSpeed: 325, letOff: 75, axleToAxle: 39, brace: 7, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Black 1', year: 2019, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Black 3', year: 2019, iboSpeed: 335, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Black 5', year: 2019, iboSpeed: 332, letOff: 80, axleToAxle: 35, brace: 7, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Black 9', year: 2019, iboSpeed: 325, letOff: 75, axleToAxle: 39, brace: 7, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Nexus 2', year: 2020, iboSpeed: 343, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Nexus 4', year: 2020, iboSpeed: 335, letOff: 80, axleToAxle: 32, brace: 6.875, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Inline 1', year: 2021, iboSpeed: 343, letOff: 80, axleToAxle: 31, brace: 6, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Inline 3', year: 2021, iboSpeed: 335, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Inline 5', year: 2021, iboSpeed: 332, letOff: 80, axleToAxle: 35, brace: 7, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Revex 2', year: 2022, iboSpeed: 343, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Revex 4', year: 2022, iboSpeed: 336, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'RVX 32', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 32, brace: 6.25, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'RVX 34', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 34, brace: 6.25, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'RVX 36', year: 2023, iboSpeed: 338, letOff: 80, axleToAxle: 36, brace: 6.5, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form', year: 2024, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Form XL', year: 2024, iboSpeed: 336, letOff: 80, axleToAxle: 34, brace: 7, category: 'hunting',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Patriot', year: 2025, iboSpeed: 320, letOff: 75, axleToAxle: 38, brace: 7, category: 'target',
    tuning: 'center-track cams' },
  { brand: 'Prime', model: 'Patriot Pro', year: 2026, iboSpeed: 322, letOff: 75, axleToAxle: 39, brace: 7, category: 'target',
    tuning: 'center-track cams' },

  // ─────────────────────────────────────────────────────────────────────
  // PSE
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'PSE', model: 'Drive R', year: 2016, iboSpeed: 348, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Drive 3B', year: 2016, iboSpeed: 332, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Brute Force', year: 2016, iboSpeed: 332, letOff: 80, axleToAxle: 32.5, brace: 7, category: 'hunting' },
  { brand: 'PSE', model: 'Carbon Air', year: 2016, iboSpeed: 348, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Stinger Extreme', year: 2016, iboSpeed: 316, letOff: 75, axleToAxle: 30.5, brace: 7.25, category: 'hunting' },
  { brand: 'PSE', model: 'Bow Madness Unleashed', year: 2017, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'PSE', model: 'EVO NTN 33', year: 2017, iboSpeed: 332, letOff: 80, axleToAxle: 33, brace: 6.625, category: 'hunting' },
  { brand: 'PSE', model: 'Carbon Air Stealth Mach 1', year: 2018, iboSpeed: 352, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'EVOLVE 28', year: 2018, iboSpeed: 333, letOff: 90, axleToAxle: 28, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'EVOLVE 31', year: 2018, iboSpeed: 333, letOff: 90, axleToAxle: 31, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'EVOLVE 35', year: 2018, iboSpeed: 326, letOff: 90, axleToAxle: 35, brace: 7, category: 'hunting' },
  { brand: 'PSE', model: 'Stinger Max', year: 2018, iboSpeed: 312, letOff: 75, axleToAxle: 30.5, brace: 7, category: 'hunting' },
  { brand: 'PSE', model: 'Drive NXT', year: 2019, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.25, category: 'hunting' },
  { brand: 'PSE', model: 'Xpedite NXT', year: 2019, iboSpeed: 360, letOff: 80, axleToAxle: 31, brace: 5.75, category: 'hunting' },
  { brand: 'PSE', model: 'Carbon Air Stealth Mach 1', year: 2019, iboSpeed: 352, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Mach E', year: 2020, iboSpeed: 348, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Brute NXT', year: 2020, iboSpeed: 326, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'EVO XF 30', year: 2020, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'EVO XF 33', year: 2020, iboSpeed: 332, letOff: 80, axleToAxle: 33, brace: 6.625, category: 'hunting' },
  { brand: 'PSE', model: 'Citation 35', year: 2020, iboSpeed: 326, letOff: 75, axleToAxle: 35, brace: 7, category: 'target' },
  { brand: 'PSE', model: 'Citation 39', year: 2020, iboSpeed: 320, letOff: 75, axleToAxle: 39, brace: 7, category: 'target' },
  { brand: 'PSE', model: 'Mach 30', year: 2021, iboSpeed: 348, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Mach 34', year: 2021, iboSpeed: 342, letOff: 80, axleToAxle: 34, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Embark', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Levitate', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Nock On Carbon Levitate', year: 2022, iboSpeed: 344, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Omen 2.0', year: 2022, iboSpeed: 366, letOff: 65, axleToAxle: 32.5, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Mach 30 ME', year: 2023, iboSpeed: 348, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Mach 34 ME', year: 2023, iboSpeed: 342, letOff: 80, axleToAxle: 34, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Carbon Levitate', year: 2023, iboSpeed: 344, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'PSE', model: 'Mach 33', year: 2024, iboSpeed: 342, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting' },
  { brand: 'PSE', model: 'Mach 30 E2', year: 2024, iboSpeed: 348, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  /* Note: there was no production 2025 PSE Sicario. The Sicario line debuted
   * in 2026 (Sicario FDS 33 and FDS 35, both DFC carbon). See the 2026
   * entries above. */
  { brand: 'PSE', model: 'Supra Focus XL', year: 2025, iboSpeed: 320, letOff: 75, axleToAxle: 40, brace: 7, category: 'target' },

  // ─────────────────────────────────────────────────────────────────────
  // BEAR
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Bear', model: 'Cruzer G2', year: 2016, iboSpeed: 315, letOff: 75, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Moment', year: 2016, iboSpeed: 335, letOff: 75, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Approach', year: 2017, iboSpeed: 320, letOff: 75, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Approach HC', year: 2017, iboSpeed: 320, letOff: 75, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Wild', year: 2017, iboSpeed: 320, letOff: 75, axleToAxle: 32, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Divergent', year: 2018, iboSpeed: 338, letOff: 75, axleToAxle: 28, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Kuma 30', year: 2018, iboSpeed: 330, letOff: 75, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Perception', year: 2018, iboSpeed: 330, letOff: 75, axleToAxle: 32.5, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Status', year: 2019, iboSpeed: 345, letOff: 75, axleToAxle: 33, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Status EKO', year: 2019, iboSpeed: 345, letOff: 90, axleToAxle: 33, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Divergent EKO', year: 2020, iboSpeed: 338, letOff: 90, axleToAxle: 28, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Refine', year: 2021, iboSpeed: 340, letOff: 75, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Refine EKO', year: 2021, iboSpeed: 340, letOff: 90, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Whitetail Legend', year: 2022, iboSpeed: 320, letOff: 75, axleToAxle: 32, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Alaskan XT', year: 2022, iboSpeed: 340, letOff: 75, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'THP Adapt', year: 2023, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'THP Adapt+', year: 2023, iboSpeed: 340, letOff: 80, axleToAxle: 32.5, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Persist', year: 2024, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Lethal Force', year: 2025, iboSpeed: 345, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Bear', model: 'Lethal Force XT', year: 2025, iboSpeed: 340, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting' },
  { brand: 'Bear', model: 'Execute', year: 2026, iboSpeed: 345, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },

  // ─────────────────────────────────────────────────────────────────────
  // BLACKOUT (Bass Pro / Cabela's house brand)
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'BlackOut', model: 'Epic', year: 2016, iboSpeed: 332, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'BlackOut', model: 'Atom', year: 2017, iboSpeed: 330, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'BlackOut', model: 'Adrenaline', year: 2018, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6.5, category: 'hunting' },
  { brand: 'BlackOut', model: 'Voltage', year: 2019, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'BlackOut', model: 'Voltage RTH', year: 2019, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'BlackOut', model: 'NX3', year: 2020, iboSpeed: 332, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'BlackOut', model: 'S3', year: 2021, iboSpeed: 340, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'BlackOut', model: 'X1', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'BlackOut', model: 'Helix RTH', year: 2023, iboSpeed: 335, letOff: 80, axleToAxle: 31, brace: 6.5, category: 'hunting' },
  { brand: 'BlackOut', model: 'Volar', year: 2024, iboSpeed: 340, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },

  // ─────────────────────────────────────────────────────────────────────
  // MISSION (Mathews-owned, budget line)
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Mission', model: 'Hype DT', year: 2016, iboSpeed: 332, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Mission', model: 'Radik', year: 2017, iboSpeed: 320, letOff: 80, axleToAxle: 30, brace: 7, category: 'hunting' },
  { brand: 'Mission', model: 'Switch', year: 2018, iboSpeed: 305, letOff: 80, axleToAxle: 31, brace: 7.125, category: 'hunting' },
  { brand: 'Mission', model: 'Craze II', year: 2018, iboSpeed: 306, letOff: 80, axleToAxle: 28, brace: 7.5, category: 'hunting' },
  { brand: 'Mission', model: 'Hammr', year: 2019, iboSpeed: 300, letOff: 80, axleToAxle: 28, brace: 6.25, category: 'hunting' },
  { brand: 'Mission', model: 'Sub-1', year: 2018, iboSpeed: 320, letOff: 80, axleToAxle: 30, brace: 7, category: 'hunting' },
  { brand: 'Mission', model: 'Ballistic', year: 2019, iboSpeed: 326, letOff: 80, axleToAxle: 31, brace: 6, category: 'hunting' },
  { brand: 'Mission', model: 'Blaze', year: 2021, iboSpeed: 295, letOff: 80, axleToAxle: 30, brace: 7, category: 'hunting' },
  { brand: 'Mission', model: 'Zone', year: 2023, iboSpeed: 308, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },

  // ─────────────────────────────────────────────────────────────────────
  // DIAMOND (Bowtech-owned, budget line)
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Diamond', model: 'Infinite Edge Pro', year: 2016, iboSpeed: 310, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Deploy SB', year: 2016, iboSpeed: 330, letOff: 80, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Provider', year: 2018, iboSpeed: 305, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Edge SB-1', year: 2018, iboSpeed: 318, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Atomic', year: 2018, iboSpeed: 230, letOff: 65, axleToAxle: 24, brace: 6, category: 'hunting' },
  { brand: 'Diamond', model: 'Infinite 305', year: 2019, iboSpeed: 305, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Edge XT', year: 2021, iboSpeed: 318, letOff: 75, axleToAxle: 30, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Edge 320', year: 2022, iboSpeed: 320, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },
  { brand: 'Diamond', model: 'Edge Max', year: 2023, iboSpeed: 320, letOff: 75, axleToAxle: 31, brace: 7, category: 'hunting' },

  // ─────────────────────────────────────────────────────────────────────
  // XPEDITION
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'Xpedition', model: 'Xcursion 6', year: 2017, iboSpeed: 354, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xcursion 7', year: 2017, iboSpeed: 344, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Xpedition', model: 'MX-15', year: 2018, iboSpeed: 354, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Xpedition', model: 'Mountaineer', year: 2019, iboSpeed: 340, letOff: 80, axleToAxle: 33.25, brace: 7, category: 'hunting' },
  { brand: 'Xpedition', model: 'Mountaineer X', year: 2019, iboSpeed: 340, letOff: 80, axleToAxle: 33.25, brace: 7, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xcursion 6 HD', year: 2019, iboSpeed: 354, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xcursion 7 HD', year: 2019, iboSpeed: 344, letOff: 80, axleToAxle: 32, brace: 7, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xlite 28', year: 2021, iboSpeed: 340, letOff: 80, axleToAxle: 28, brace: 6, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xlite 32', year: 2021, iboSpeed: 340, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting' },
  { brand: 'Xpedition', model: 'Xlite 33', year: 2022, iboSpeed: 340, letOff: 80, axleToAxle: 33, brace: 6.75, category: 'hunting' },
  { brand: 'Xpedition', model: 'Smoke X', year: 2023, iboSpeed: 350, letOff: 80, axleToAxle: 30, brace: 6, category: 'hunting' },
  { brand: 'Xpedition', model: 'Apex', year: 2024, iboSpeed: 345, letOff: 80, axleToAxle: 32, brace: 6.5, category: 'hunting' },

  // ─────────────────────────────────────────────────────────────────────
  // APA
  // ─────────────────────────────────────────────────────────────────────
  { brand: 'APA', model: 'Mamba M7', year: 2017, iboSpeed: 350, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'APA', model: 'Mamba M8', year: 2018, iboSpeed: 350, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'APA', model: 'King Cobra X7', year: 2018, iboSpeed: 358, letOff: 80, axleToAxle: 31, brace: 5.875, category: 'hunting' },
  { brand: 'APA', model: 'Mamba X', year: 2019, iboSpeed: 358, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'APA', model: 'Mamba 33', year: 2020, iboSpeed: 350, letOff: 80, axleToAxle: 33, brace: 6.5, category: 'hunting' },
  { brand: 'APA', model: 'Mamba Onyx', year: 2022, iboSpeed: 358, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },
  { brand: 'APA', model: 'Mamba EX', year: 2024, iboSpeed: 358, letOff: 80, axleToAxle: 32, brace: 6, category: 'hunting' },

] as const;

/** Unique bow brands present in the catalog (for dropdowns). */
export const BOW_BRANDS = Array.from(new Set(BOWS.map((b) => b.brand))).sort();

/** Bow category — used for filter UI in the picker. */
export type BowCategory = 'hunting' | 'target';

/** Effective category for a bow (defaults to 'hunting' when unspecified). */
export function bowCategory(bow: BowModel): BowCategory {
  return bow.category ?? 'hunting';
}

/** Filter the catalog by hunting vs target category. */
export function bowsByCategory(category: BowCategory): readonly BowModel[] {
  return BOWS.filter((b) => bowCategory(b) === category);
}

/** Lookup helper — fuzzy match brand + model (case-insensitive). */
export function findBow(brand: string, model: string, year?: number): BowModel | undefined {
  const b = brand.trim().toLowerCase();
  const m = model.trim().toLowerCase();
  const matches = BOWS.filter(
    (bow) => bow.brand.toLowerCase() === b && bow.model.toLowerCase() === m,
  );
  if (year) return matches.find((bow) => bow.year === year) ?? matches[0];
  // Without year, prefer newest
  return matches.sort((a, b) => b.year - a.year)[0];
}
